/* eslint-disable react/jsx-wrap-multilines, react/jsx-one-expression-per-line */
import React, { useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// React Router
import { useHistory } from 'react-router-dom';

// Material
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CardMedia from '@material-ui/core/CardMedia';

// Icons
import MoreHorizIcon from 'components/Icons/MoreHoriz';
import ChartIcon from 'components/Icons/Chart';
import DeleteIcon from 'components/Icons/Delete';

// Material Theme
import { useTheme } from '@material-ui/core/styles';

// Clsx
import clsx from 'clsx';

// Google Charts
import Chart from 'react-google-charts';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as StopsActions from 'store/ducks/stops/actions';

// Types
import { ApplicationState } from 'store';

// Helpers
import generateGraphData from 'helpers/generateGraphData';

// Constants
import IMAGES from 'constants/images';
import PATH from 'constants/basePath';

// Components
import { DialogContentYesOrNo } from 'components';
import { ChartSkeleton } from 'components/ChartSkeleton';
import { LineSkeleton } from 'components/LineSkeleton';
import { StopDrawer } from 'components/StopDrawer';

// Styles
import useStyle from './stopNavigation.styles';

interface StateProps {
  stopId: string;
}

type Props = StateProps;

const StopNavigation: React.FC<Props> = ({ stopId }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();

  const stopData = useSelector(
    (state: ApplicationState) => state.stops.stopData,
  );

  const startDate = useSelector(
    (state: ApplicationState) => state.dates.selectedStartDate,
  );

  const endDate = useSelector(
    (state: ApplicationState) => state.dates.selectedEndDate,
  );

  const stopLoading = useSelector(
    (state: ApplicationState) => state.stops.loading,
  );

  const stopReportsList = useSelector(
    (state: ApplicationState) => state.stops.stopReports,
  );

  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    if (stopId) {
      dispatch(
        StopsActions.getStopRequest({ id: stopId, _includes: 'line,address' }),
      );
    }
  }, [dispatch, stopId]);

  useEffect(() => {
    if (stopData.id) {
      const now = new Date();
      const start = new Date();
      start.setHours(start.getHours() - 4);
      dispatch(
        StopsActions.getStopReportsRequest({
          reportType: 'stop_in_out',
          stopId: stopData.id,
          startDate: start.getTime(),
          endDate: now.getTime(),
        }),
      );
    }
  }, [dispatch, stopData, startDate, endDate]);

  useEffect(() => {
    if (stopReportsList.length > 0) {
      const now = new Date();
      const start = new Date();
      start.setHours(start.getHours() - 4);
      setChartData(
        generateGraphData
          .groupByHour(stopReportsList)
          .slice(start.getHours(), now.getHours())
          .map((item: any) => [
            `${item.hour}h`,
            item.entry.total,
            item.exit.total,
          ]),
      );
    }
  }, [stopReportsList]);

  if (!stopId || !stopData) {
    return <></>;
  }

  return (
    <StopDrawer
      title={stopData.name}
      stopId={stopData.id}
      classes={{
        root: classes.root,
        header: clsx({ [classes.resetPdBottom]: !stopData.Address }),
      }}
      body={
        <Box
          flexGrow={1}
          p={2}
          className={clsx({ [classes.resetPdTop]: !stopData.Address })}>
          {stopData.Address && (
            <Grid
              container
              justifyContent='space-between'
              alignItems='center'
              className={classes.cardAddress}>
              <Grid item lg={9} md={9} sm={9} xl={9} xs={9}>
                <Grid container direction='column'>
                  <Typography color='primary' display='inline'>
                    endereço:{' '}
                  </Typography>
                  <Typography display='inline'>
                    {stopData.Address.street} • {stopData.Address.number}
                  </Typography>
                  <Typography display='inline'>
                    {stopData.Address.city} • {stopData.Address.state}
                  </Typography>
                </Grid>
              </Grid>

              <Grid
                item
                lg={3}
                md={3}
                sm={3}
                xl={3}
                xs={3}
                className={classes.cardAddressImage}>
                <CardMedia component='img' src={IMAGES.BOX_BUS} />
              </Grid>
            </Grid>
          )}

          <Grid container direction='column' className={classes.margins}>
            <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
              <Typography color='primary' display='inline'>
                coberto:{' '}
              </Typography>
              <Typography display='inline'>
                {stopData.covered ? 'sim' : 'não'}
              </Typography>
            </Grid>

            <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
              <Typography color='primary' display='inline'>
                baia:{' '}
              </Typography>
              <Typography display='inline'>
                {stopData.busBay ? 'sim' : 'não'}
              </Typography>
            </Grid>

            <Grid container direction='column' className={classes.margins}>
              <Typography color='primary' display='inline'>
                coordenadas:
              </Typography>

              <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
                <Typography display='inline'>
                  latitude inicial • {stopData.startLat}
                </Typography>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
                <Typography display='inline'>
                  longitude inicial • {stopData.startLong}
                </Typography>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
                <Typography display='inline'>
                  latitude final • {stopData.endLat}
                </Typography>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xl={12} xs={12}>
                <Typography display='inline'>
                  longitude final • {stopData.endLong}
                </Typography>
              </Grid>
            </Grid>

            <Grid container direction='column' className={classes.margins}>
              <Typography className={classes.label}>
                embarque e desembarque
              </Typography>

              <Grid
                container
                className={clsx(classes.chartContainer, {
                  [classes.noContent]: !chartData.length,
                })}>
                {stopLoading ? (
                  <ChartSkeleton showLegend />
                ) : (
                  <>
                    {!chartData.length && (
                      <Typography color='primary'>
                        Nao há dados de embarque/desembarque para este ponto
                      </Typography>
                    )}

                    {chartData.length > 0 && (
                      <>
                        <Chart
                          width={450}
                          height={200}
                          chartType='LineChart'
                          loader={<ChartSkeleton />}
                          data={[
                            ['x', 'embarque', 'desembarque'],
                            ...chartData,
                          ]}
                          options={{
                            hAxis: {
                              textPosition: 'bottom',
                            },
                            vAxis: {
                              textPosition: 'none',
                            },
                            backgroundColor: theme.palette.colors.gray5,
                            colors: [
                              theme.palette.colors.greenDegrade,
                              theme.palette.colors.darkBlue,
                            ],
                            legend: {
                              position: 'none',
                            },
                            enableInteractivity: true,
                            chartArea: {
                              width: '95%',
                              height: 'auto',
                            },
                          }}
                        />

                        <Grid
                          container
                          alignItems='center'
                          className={classes.legend}>
                          <Grid item lg={4} md={4} sm={4} xl={4} xs={4}>
                            <Grid container alignItems='center'>
                              <Grid
                                className={clsx(
                                  classes.square,
                                  classes.squareGreen,
                                )}
                              />
                              <Typography className={classes.legendText}>
                                embarque
                              </Typography>
                            </Grid>
                          </Grid>

                          <Grid item lg={8} md={8} sm={8} xl={8} xs={8}>
                            <Grid container alignItems='center'>
                              <Grid
                                className={clsx(
                                  classes.square,
                                  classes.squareDarkBlue,
                                )}
                              />
                              <Typography className={classes.legendText}>
                                desembarque
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>

            {stopData.Lines!?.length > 0 && (
              <Grid container direction='column'>
                <Typography className={classes.label}>
                  linhas que passam por aqui
                </Typography>

                {stopLoading ? (
                  <LineSkeleton generateCount={3} />
                ) : (
                  <>
                    {stopData.Lines &&
                      stopData.Lines.map((line, index) => (
                        <Grid
                          key={line.id}
                          container
                          alignItems='center'
                          className={clsx(classes.lineContainer, {
                            [classes.even]: index % 2 === 0,
                          })}>
                          <Box
                            style={{ background: line.color }}
                            className={clsx(classes.line)}
                          />
                          <Typography>{line.name}</Typography>
                        </Grid>
                      ))}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      }
      footer={
        <>
          <span className={classes.registrationDate}>
            Data de registro:{' '}
            {stopData.createdAt
              ? new Date(stopData.createdAt).toLocaleString('pt-BR')
              : ''}
          </span>
          <Divider variant='fullWidth' />

          <Box flexGrow={0} p={2}>
            <Box alignContent='center' display='flex' flexDirection='row'>
              <Box>
                <Tooltip placement='top' title='Editar'>
                  <IconButton
                    onClick={() => {
                      history.push(
                        `${PATH.STOPS.BASE_URL}/editar/${stopData.id}`,
                      );
                    }}>
                    <MoreHorizIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                <Tooltip
                  placement='top'
                  title='Ver gráfico de embarque/desembarque'>
                  <IconButton
                    onClick={() => {
                      history.push(
                        `${PATH.STOPS.BASE_URL}/charts/${stopData.id}`,
                      );
                    }}>
                    <ChartIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                <Tooltip placement='top' title='Deletar'>
                  <IconButton
                    onClick={() => {
                      dispatch(
                        DialogsActions.setOpen({
                          open: true,
                          content: (
                            <DialogContentYesOrNo
                              image={IMAGES.TRASH_IMAGE_URL}
                              message={
                                <Typography
                                  className={classes.message}
                                  variant='body1'>
                                  Tem certeza que deseja deletar a parada de
                                  nome
                                  <span className={classes.infos}>
                                    {' '}
                                    {stopData.name}
                                  </span>
                                  ?
                                </Typography>
                              }
                              yesOnClick={() => {
                                dispatch(
                                  StopsActions.deleteStopRequest({
                                    stopId: stopData.id,
                                  }),
                                );
                              }}
                            />
                          ),
                        }),
                      );
                    }}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </>
      }
    />
  );
};

export { StopNavigation };
export default StopNavigation;
